import {
  APIComponentAttributes,
  ComponentOptionsResponse,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { BreadcrumbData } from '@ardoq/basic-components';
import { Locale, localeCompareNumeric } from '@ardoq/locale';
import { SelectOptionsOrGroups } from '@ardoq/select';
import { Fields } from '../../types';
import { getIdentifierStringFromComponentValue } from '../questionOperations';
import { IconName } from '@ardoq/icons';
import { type I18nLocale, t } from '@ardoq/i18n';
import { IdentifierWithPopover } from './questionOptionsAtoms';
import { createElement } from 'react';
import { ComponentOption } from './questionOptionsTypes';

const HELP_MESSAGE_VALUE = 'help-text';

const getLimitOption = (total: number, limit: number): ComponentOption => ({
  label: `Showing ${limit} of ${total} items. Type to narrow your search.`,
  value: HELP_MESSAGE_VALUE,
  iconName: IconName.INFO,
  isDisabled: true,
});

const OPTION_LIMIT = 100;
const TAG_OPTION_LIMIT = 500;

const prependLimitInfoIfAboveLimit = (
  options: ComponentOption[],
  total: number
) => {
  if (total > OPTION_LIMIT) {
    return [getLimitOption(total, OPTION_LIMIT), ...options];
  }
  return options;
};

export const limitOptions = (options: SelectOptionsOrGroups<string>) => {
  if (options.length > TAG_OPTION_LIMIT) {
    return [
      getLimitOption(options.length, TAG_OPTION_LIMIT),
      ...options.slice(0, TAG_OPTION_LIMIT),
    ];
  }
  return options;
};

const namePathToBreadcrumbs = (namePath: string[]): BreadcrumbData[] => {
  return namePath.map(text => ({ text }));
};

const toComponentSelectOption = (
  component: APIComponentAttributes,
  identifierFieldName: keyof APIComponentAttributes | undefined,
  fields: Fields,
  locale: Locale
): ComponentOption => {
  const { _id, name, ardoq } = component;
  const namePath = ardoq.namePath;
  const identifierValue: string | number | boolean | null | undefined =
    identifierFieldName ? component[identifierFieldName] : undefined;
  const identifierString = getIdentifierStringFromComponentValue(
    identifierValue,
    fields,
    identifierFieldName,
    locale
  );

  return {
    value: _id,
    label: name,
    breadcrumbs:
      namePath && namePath.length > 1
        ? namePathToBreadcrumbs(namePath)
        : undefined,
    rightContent: createElement(IdentifierWithPopover, {
      identifierString,
    }),
    identifierString,
  };
};

const AI_SUGGESTED_OPTION_ID = 'ai-suggestion-option';

const toAISuggestedComponentSelectOption = (
  component: APIComponentAttributes,
  identifierFieldName: string | undefined,
  fields: Fields,
  locale: Locale
): ComponentOption => ({
  ...toComponentSelectOption(component, identifierFieldName, fields, locale),
  isAi: true,
  testId: AI_SUGGESTED_OPTION_ID, // Used for tracking purposes
});

const alphabetically = (
  a: ComponentOption,
  b: ComponentOption,
  locale: Locale
) =>
  localeCompareNumeric(
    optionToComparableString(a),
    optionToComparableString(b),
    locale
  );

const optionToComparableString = (option: ComponentOption): string => {
  if (!option.breadcrumbs) {
    return option.label;
  }
  return option.breadcrumbs.map(breadcrumb => breadcrumb.text).join(' ');
};

export const prepareOptionsWithLimit = (
  optionsResponse: ComponentOptionsResponse,
  identifierFieldName: string | undefined,
  fields: Fields,
  locale: Locale,
  shouldSort: boolean
) => {
  const components = optionsResponse.components;
  const options: ComponentOption[] = components
    .map((comp: APIComponentAttributes) =>
      toComponentSelectOption(comp, identifierFieldName, fields, locale)
    )
    .sort((a: ComponentOption, b: ComponentOption) => {
      if (shouldSort) return alphabetically(a, b, locale);
      return 0;
    })
    .filter(isTruthy);

  const totalOptionsMatchingQuery = optionsResponse.total;
  return prependLimitInfoIfAboveLimit(options, totalOptionsMatchingQuery);
};

export const prepareAiOptionsgroup = (
  aiResponse: APIComponentAttributes[],
  identifierFieldName: string | undefined,
  fields: Fields,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  const aiSuggestedOptions = aiResponse.map(suggestion =>
    toAISuggestedComponentSelectOption(
      suggestion,
      identifierFieldName,
      fields,
      locale
    )
  );
  return {
    label: t(i18nLocale, 'surveysUi:options.aiSuggestions', 'AI suggestions'),
    leftIcon: { name: IconName.AI_STAR },
    options: aiSuggestedOptions,
  };
};
